import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription } from 'rxjs';
import { MoneyService } from '../money.service';
import {
  SavingsGoal, Projection, DEFAULT_PROJECTION,
  RATE_MIN_BP, RATE_MAX_BP, RATE_STEP_BP, YEARS_MIN, YEARS_MAX,
} from '../money.model';
import { formatCents } from '../format';
import { toCents, todayISO } from '../input';
import { LinePoint } from '../charts/line-chart.component';
import {
  SavingsChip, monthsToGoal, targetDate, savingsRateChips,
  projectBalance, milestoneRows,
} from './savings.calc';

@Component({
  selector: 'app-money-savings',
  templateUrl: './savings.component.html',
  styleUrls: ['./savings.component.scss'],
  standalone: false,
})
export class SavingsComponent implements OnInit, OnDestroy {
  loading = true;
  error = '';

  goals: SavingsGoal[] = [];
  chips: SavingsChip[] = [];
  projection: Projection = { ...DEFAULT_PROJECTION };

  /** The add-goal form. Dollars as typed; converted to cents on submit. */
  draft = { name: '', target: '', monthly: '' };

  /**
   * The projection's dollar fields as typed. Held as strings rather than bound
   * through a formatting getter/setter pair: a setter that reformats on every
   * keystroke rewrites the input mid-edit and throws the caret to the end, so
   * "1870" becomes unenterable. Seeded from the stored projection on load and
   * parsed to cents on commit.
   */
  projDraft = { start: '', monthly: '' };

  readonly fmt = formatCents;
  readonly rateMin = RATE_MIN_BP;
  readonly rateMax = RATE_MAX_BP;
  readonly rateStep = RATE_STEP_BP;
  readonly yearsMin = YEARS_MIN;
  readonly yearsMax = YEARS_MAX;

  private uid = '';
  private projSeeded = false;
  private subs: Subscription[] = [];
  private innerSubs: Subscription[] = [];

  constructor(private afAuth: AngularFireAuth, private money: MoneyService) {}

  ngOnInit(): void {
    this.subs.push(this.afAuth.authState.subscribe(user => {
      if (!user) return;
      this.uid = user.uid;
      // Reset here, not just at field init: a sign-out then sign-in within
      // this same component instance re-enters this handler, and the next
      // user's stored projection must seed projDraft too — otherwise the
      // previous user's dollar amounts would stay on screen unseeded.
      this.projSeeded = false;

      // Release the previous inner subscriptions before creating new ones.
      // accounts.component.ts and recurring.component.ts push theirs on
      // every authState emission without releasing the prior pair, so a
      // re-login leaves two live streams writing the same fields — that's
      // the house pattern, but it's wrong, and this screen deliberately
      // diverges from it rather than repeating it a third time.
      this.innerSubs.forEach(s => s.unsubscribe());
      this.innerSubs = [];

      this.innerSubs.push(this.money.subscribeAll(this.uid).subscribe({
        next: data => { this.chips = savingsRateChips(data.txns, todayISO()); },
        error: (e: unknown) => this.fail(e),
      }));

      this.innerSubs.push(this.money.subscribeSavings(this.uid).subscribe({
        next: data => {
          this.goals = data.goals;
          // The goal under edit may have been removed elsewhere (another tab,
          // or a delete that raced this edit). Leaving editingId set would
          // let saveEdit() update() a dead path, resurrecting a partial node
          // with no createdAt — same class of ghost-node write applyBulk's
          // liveIds guard exists to prevent in transactions.component.ts.
          if (this.editingId && !this.goals.some(g => g.id === this.editingId)) {
            this.editingId = null;
          }
          this.projection = { ...data.projection };
          // Seed the text fields once from storage; afterward they are the
          // user's own in-progress edit and must not be overwritten by a
          // background emission.
          if (!this.projSeeded) {
            this.projDraft = {
              start: (data.projection.startCents / 100).toFixed(2),
              monthly: (data.projection.monthlyCents / 100).toFixed(2),
            };
            this.projSeeded = true;
          }
          this.loading = false;
        },
        error: (e: unknown) => this.fail(e),
      }));
    }));
  }

  // The authState subscription itself is kept alive in `subs` for the life of
  // the component (an unstored one outlives the component and re-runs
  // against a destroyed instance on the next auth event); `innerSubs` holds
  // the two per-login streams, released above on every re-login and again
  // here on destroy.
  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.innerSubs.forEach(s => s.unsubscribe());
  }

  private fail(e: unknown): void {
    this.error = `Could not load your savings: ${(e as Error).message}. Try reloading the page.`;
    this.loading = false;
  }

  /** Shared by the add form and the edit-in-place row: a name and a positive target. */
  private isValidGoalDraft(d: { name: string; target: string }): boolean {
    return d.name.trim().length > 0 && toCents(d.target) > 0;
  }

  get canAdd(): boolean {
    return this.isValidGoalDraft(this.draft);
  }

  async addGoal(): Promise<void> {
    if (!this.canAdd) return;
    try {
      await this.money.addGoal(this.uid, {
        name: this.draft.name.trim(),
        targetCents: toCents(this.draft.target),
        monthlyCents: toCents(this.draft.monthly),
        createdAt: Date.now(),
      });
      this.draft = { name: '', target: '', monthly: '' };
    } catch (e) { this.fail(e); }
  }

  async removeGoal(goal: SavingsGoal): Promise<void> {
    try { await this.money.deleteGoal(this.uid, goal.id); } catch (e) { this.fail(e); }
  }

  editingId: string | null = null;
  editDraft = { name: '', target: '', monthly: '' };

  startEdit(g: SavingsGoal): void {
    this.editingId = g.id;
    this.editDraft = {
      name: g.name,
      target: (g.targetCents / 100).toFixed(2),
      monthly: (g.monthlyCents / 100).toFixed(2),
    };
  }
  cancelEdit(): void { this.editingId = null; }

  get canSaveEdit(): boolean {
    return this.isValidGoalDraft(this.editDraft);
  }

  async saveEdit(): Promise<void> {
    if (!this.editingId || !this.canSaveEdit) return;
    try {
      await this.money.updateGoal(this.uid, this.editingId, {
        name: this.editDraft.name.trim(),
        targetCents: toCents(this.editDraft.target),
        monthlyCents: toCents(this.editDraft.monthly),
      });
      this.editingId = null;
    } catch (e) { this.fail(e); }
  }

  /** Chips fill the draft field; the user is free to overwrite it afterward. */
  useChip(chip: SavingsChip): void {
    this.draft.monthly = (chip.monthlyCents / 100).toFixed(2);
  }

  monthsFor(goal: SavingsGoal): number | null {
    return monthsToGoal(goal.targetCents, goal.monthlyCents);
  }

  /**
   * Explains a null monthsFor(): a negative monthly figure is a set amount
   * (a chip can legitimately be negative — real monthly net has run as low
   * as -$861) that just can't reach the goal, which reads very differently
   * from never having entered one at all.
   */
  noDateReason(goal: SavingsGoal): string {
    return goal.monthlyCents < 0
      ? "won't reach the goal at this rate"
      : 'set a monthly amount';
  }

  dateFor(goal: SavingsGoal): string {
    const months = this.monthsFor(goal);
    return months === null ? '' : targetDate(todayISO(), months);
  }

  trackGoal(_: number, g: SavingsGoal): string { return g.id; }

  /** Percent for display; the model stores basis points so no float is written. */
  get ratePercent(): string {
    return (this.projection.rateBasisPoints / 100).toFixed(2).replace(/\.?0+$/, '') + '%';
  }

  /**
   * `projection.years` bounded to [YEARS_MIN, YEARS_MAX] and coerced to an
   * integer, read fresh on every access rather than only at commit time.
   *
   * [(ngModel)] on the years input writes the model on every keystroke, but
   * commitProjection's clamp runs on (change) — i.e. on blur. Without a
   * clamp at the read site too, a pasted or fat-fingered six- or
   * seven-digit value reaches projectBalance (via projectionSeries and
   * milestones, both read on every change-detection pass) before blur ever
   * fires, allocating hundreds of thousands to millions of objects several
   * times over. NaN, blanks, zero, and negatives all fall back to
   * YEARS_MIN rather than producing an empty or exploding series.
   */
  /** Public: the caption and aria-label must report the horizon actually plotted. */
  get boundedYears(): number {
    const n = Math.round(this.projection.years);
    if (!Number.isFinite(n) || n < this.yearsMin) return this.yearsMin;
    return Math.min(n, this.yearsMax);
  }

  get projectionSeries(): { year: number; cents: number }[] {
    const p = this.projection;
    return projectBalance(p.startCents, p.monthlyCents, p.rateBasisPoints, this.boundedYears);
  }

  /** Live preview while typing, before (change) commits: parse without saving. */
  previewProjection(): void {
    this.projection.startCents = toCents(this.projDraft.start);
    this.projection.monthlyCents = toCents(this.projDraft.monthly);
  }

  get projectionPoints(): LinePoint[] {
    // Charts never format money — displayValues carries the text.
    return this.projectionSeries.map(s => ({ label: `${s.year}y`, value: s.cents }));
  }

  get projectionDisplay(): string[] {
    return this.projectionSeries.map(s => formatCents(s.cents));
  }

  get milestones(): { years: number; cents: number }[] {
    const p = this.projection;
    return milestoneRows(p.startCents, p.monthlyCents, p.rateBasisPoints, this.boundedYears);
  }

  get endingBalanceCents(): number {
    const series = this.projectionSeries;
    return series[series.length - 1]?.cents ?? 0;
  }

  /**
   * Parse the dollar fields into the cents model, then persist.
   *
   * Called from (change), which fires on blur for the text inputs and on
   * release for the slider — not on every keystroke or drag tick, which would
   * be one RTDB write per pixel.
   */
  async commitProjection(): Promise<void> {
    this.projection.startCents = toCents(this.projDraft.start);
    this.projection.monthlyCents = toCents(this.projDraft.monthly);
    this.projection.years = Math.min(
      Math.max(Math.round(this.projection.years) || this.yearsMin, this.yearsMin),
      this.yearsMax);

    if (!this.uid) return;
    try { await this.money.saveProjection(this.uid, this.projection); }
    catch (e) { this.fail(e); }
  }
}
